'use client';

import { useState, useEffect, lazy, Suspense, useRef } from 'react';
import { File } from 'lucide-react';
import { motion } from 'framer-motion';
import MouseScroll from '@/components/ui/ScrollIndicator';
import AnimatedELogo from '@/components/ui/AnimatedELogo';
import AnimatedJLogo from '@/components/ui/AnimatedJLogo';
import AnimatedMalekLogo from '@/components/ui/AnimatedMalekLogo';
import Link from 'next/link';
import { Button } from '@heroui/button';
import WrapButton from '@/components/ui/wrap-button';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

// Lazy load particles with minimal delay for LCP
const LazyParticles = lazy(() => import('@/components/ui/Particles'));

export default function HomeHero() {
  const titleRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [scrollY] = useState(0);
  const [showParticles, setShowParticles] = useState(false);

  useEffect(() => {
    // Enable particles after component mounts
    setShowParticles(true);
  }, []);

  useEffect(() => {
    // Setup scroll animations for the hero section
    if (typeof window === 'undefined') return;

    const heroSection = document.querySelector('.hero-section');
    if (!heroSection) return;

    // Intro animations timeline
    const introTl = gsap.timeline();

    // Set initial states
    gsap.set([titleRef.current, contentRef.current], {
      opacity: 0,
      y: 30,
    });

    // Animate title and description in sequence
    introTl
      .to(titleRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        delay: 0.3,
        ease: 'power2.out',
      })
      .to(
        contentRef.current,
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          delay: 0.1, // Small gap between title and description
          ease: 'power2.out',
        },
        '-=0.4',
      ); // Slight overlap for smooth flow

    // Scroll animations timeline
    gsap.timeline({
      scrollTrigger: {
        trigger: heroSection,
        start: 'top top',
        end: '75% top',
        scrub: 1,
        pin: true,
        pinSpacing: true,
        anticipatePin: 1,
        onUpdate: self => {
          const progress = self.progress;
          gsap.set(heroSection, {
            scale: Math.max(0.8, 1 - progress * 0.3),
            opacity: Math.max(0.0, 1 - progress * 0.8),
            y: progress * 50,
          });
        },
      },
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
      introTl.kill();
    };
  }, []);

  return (
    <div className="hero-section relative top-0 mb-8 flex min-h-screen w-full items-center justify-center overflow-hidden px-10 xl:h-screen">
      {/* Particles - Minimal delay for LCP */}
      {showParticles && (
        <Suspense fallback={null}>
          <motion.div
            className="pointer-events-none fixed top-0 left-0 h-full w-full"
            style={{
              opacity: Math.max(0, 1 - scrollY * 0.001),
              transition: 'opacity 0.1s ease-out',
            }}
          >
            <LazyParticles
              className="absolute inset-0"
              quantity={15}
              ease={20}
            />
          </motion.div>
        </Suspense>
      )}

      {/* Hero Content */}
      <div className="z-50 mx-auto flex max-w-7xl flex-col px-4 text-center xl:py-16">
        <div className="content-container w-full">
          {/* Animated Logos */}
          <div className="relative mx-auto flex flex-col items-center justify-center gap-2 px-12 sm:flex-row">
            <div className="flex flex-row items-center justify-center">
              <AnimatedELogo
                width={70}
                height={57}
                className="sm:h-[53px] sm:w-[70px] md:h-[100px] md:w-[120px] lg:h-[125px] lg:w-[150px] xl:h-[150px] xl:w-[180px]"
                strokeWidth={8}
                duration={0.3}
              />
              <AnimatedJLogo
                width={70}
                height={53}
                className="sm:h-[59px] sm:w-[70px] md:h-[95px] md:w-[120px] lg:h-[118px] lg:w-[150px] xl:h-[140px] xl:w-[170px]"
                strokeWidth={8}
                duration={0.2}
              />
            </div>

            <AnimatedMalekLogo
              width={180}
              height={55}
              className="sm:h-[74px] sm:w-[230px] md:h-[113px] md:w-[300px] lg:ml-8 lg:h-[130px] lg:w-[350px] xl:h-[160px] xl:w-[400px]"
              strokeWidth={8}
              duration={0.4}
            />
          </div>

          {/* Title  */}
          <div
            ref={titleRef}
            className="antonio-400 mt-2 mb-12 text-3xl md:text-5xl lg:text-6xl xl:text-6xl"
          >
            <span className="text-[oklch(0.5465_0.2455_262.8657)]">
              SOLUTIONS
            </span>
            <span> ARCHITECT </span>
            <span className="text-[oklch(0.5465_0.2455_262.8657)]"> | </span>
            <span> AI </span>
            <span className="text-[oklch(0.5465_0.2455_262.8657)]">
              {' '}
              ENGINEER{' '}
            </span>
          </div>

          {/* Description  */}
          <p
            ref={contentRef}
            className="mx-auto mb-12 max-w-4xl px-6 text-xl leading-relaxed font-normal lg:text-2xl xl:text-3xl 2xl:text-4xl"
          >
            <span className="inline-block">
              Transforming businesses with intelligent AI solutions and
              future-ready architectures.
            </span>
          </p>
        </div>
        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.75 }}
          className="z-50 mx-auto flex h-full w-full flex-col items-center justify-center gap-4 sm:flex-row 2xl:pt-12"
        >
          <Link href="/projects" aria-label="View Projects">
            <WrapButton>
              <span className="relative z-10 flex items-center justify-center gap-2">
                View Projects
              </span>
            </WrapButton>
          </Link>
          <a
            href="/uploads/EJ_Malek_SolutionArchitect.pdf"
            download="EJ_Malek_SolutionArchitect.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              aria-label="Resume"
              color="primary"
              variant="light"
              size="lg"
              className="hover:bg-primary/10 border-primary/30 hover:border-primary/30 rounded-full border bg-transparent transition-all duration-300 hover:scale-105"
            >
              <span className="text-chart-3 group-hover:text-foreground relative z-10 flex items-center justify-center gap-2">
                Resume
                <File className="group-hover:text-primary text-foreground h-4 w-4 transition-all duration-300 hover:rotate-12" />
              </span>
            </Button>
          </a>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <div
        className="data-[state=show]:animate-in data-[state=hide]:animate-out fade-in slide-in-from-top-8 fade-out slide-out-to-top-8 absolute bottom-0 flex h-full w-full items-center justify-center duration-300 lg:bottom-10"
        data-state="show"
        style={{
          opacity: Math.max(0, 1 - scrollY * 0.001),
          transition: 'opacity 0.1s ease-out',
        }}
      >
        <MouseScroll />
      </div>
    </div>
  );
}
